import type { CityConfig } from "../city-template/types";

export const moreheadConfig: CityConfig = {
  city: "Morehead",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "morehead",
  route: "/morehead",
  title: "Emergency Passport Morehead, KY — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Morehead, KY — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Rowan County and Eastern Kentucky travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Morehead, KY",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Morehead and Eastern Kentucky travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Morehead%20Kentucky%20downtown%20with%20historic%20buildings%20and%20Morehead%20State%20University%20campus%20visible%2C%20beautiful%20Eastern%20Kentucky%20Appalachian%20foothills%20landscape%2C%20warm%20golden%20hour%20light%20with%20charming%20small%20college%20town%20atmosphere%2C%20clean%20modern%20cityscape%20photography%2C%20wide%20panoramic%20view%20with%20rolling%20green%20hills%2C%20no%20visible%20text%20or%20people%2C%20vibrant%20Rowan%20County%20scene%20with%20Daniel%20Boone%20National%20Forest%20in%20the%20distance&width=1800&height=700&seq=morehead-hero-001&orientation=landscape",
  heroImageAlt: "Morehead Kentucky downtown with Morehead State University campus and Eastern Kentucky hills",
  geoPosition: "38.1837;-83.4326",
  geoPlacename: "Morehead, Kentucky",
  lastModified: "2026-07-21",
  regionalAgency: {
    name: "Atlanta Passport Agency",
    address: "230 Peachtree St NW, Suite 1000, Atlanta, GA 30308",
    distance: "Located in downtown Atlanta — approximately 360 miles from Morehead via I-75",
  },
  nearbyCitiesZips: [
    { name: "Morehead", zips: "40351" },
    { name: "Mount Sterling", zips: "40353" },
    { name: "West Liberty", zips: "41472" },
    { name: "Salyersville", zips: "41465" },
    { name: "Paintsville", zips: "41240" },
    { name: "Prestonsburg", zips: "41653" },
    { name: "Lexington", zips: "40502–40517" },
    { name: "Ashland", zips: "41101, 41102" },
    { name: "Huntington (WV)", zips: "25701–25779" },
    { name: "Maysville", zips: "41056" },
    { name: "Winchester", zips: "40391, 40392" },
    { name: "Paris", zips: "40361, 40362" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Rowan County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Lexington", route: "/lexington" },
    { name: "Fast passport renewal in Ashland", route: "/ashland" },
    { name: "Emergency passport help in Richmond", route: "/richmond" },
    { name: "Urgent passport services in Berea", route: "/berea" },
    { name: "Same-day passport help in Winchester", route: "/winchester" },
    { name: "Passport courier services near Frankfort", route: "/frankfort" },
  ],
  fpcOfficeAddress: "500 W Jefferson St, Louisville, KY 40202",
  fpcOfficeNote: "Fast Passport Center serves Morehead remotely via secure courier. We do not have a walk-in office in Morehead — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Rowan County travelers.",
  fpcOfficeDistance: "Serving all of Rowan County and Eastern Kentucky",
  serviceAreaIntro: "From downtown Morehead's historic district to the Morehead State University campus, from the Appalachian foothills to the Daniel Boone National Forest area — Fast Passport Center serves the entire Morehead community. We regularly help travelers from Mount Sterling, West Liberty, and Ashland get their passports fast.",
  nearbyCitiesIntro: "We serve all Morehead neighborhoods and nearby Eastern Kentucky cities including Mount Sterling, West Liberty, Salyersville, Paintsville, Prestonsburg, Lexington, Ashland, Huntington (WV), Maysville, Winchester, and Paris. If you are in Rowan County, Eastern Kentucky, or the Appalachian region, we have you covered.",
  howItWorksIntro: "Most Morehead travelers complete everything online and ship documents overnight. No need to drive to Atlanta or wait in line at the regional agency.",
  mistakesIntro: "Morehead travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Morehead and Eastern Kentucky travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Morehead? Here are the most common ones we hear from Rowan County travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  dmvWebsite: "https://drive.ky.gov",
  dmvLabel: "Kentucky Transportation Cabinet — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Rowan County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Rachel C.",
      location: "Morehead, KY",
      rating: 5,
      text: "My study abroad program to Italy was starting in 5 days and my passport was expired. Fast Passport Center got my renewal in 2 days. I made my flight to Rome and had the best semester of my life.",
      service: "Passport Renewal",
    },
    {
      name: "Michael B.",
      location: "Mount Sterling, KY",
      rating: 5,
      text: "Emergency trip to Germany for my mother's funeral. I had no idea what to do. The team handled everything and got my passport in 36 hours. I made it to say goodbye.",
      service: "Emergency Passport",
    },
    {
      name: "Emily W.",
      location: "West Liberty, KY",
      rating: 5,
      text: "First-time passport for our family cruise to the Bahamas. They helped me with every form and the acceptance clerk appointment. Had our passports in 5 days. Amazing service.",
      service: "New Passport",
    },
    {
      name: "Daniel S.",
      location: "Ashland, KY",
      rating: 5,
      text: "Lost my passport before a business trip to Toronto. They had a replacement in 24 hours. I didn't even have to drive to Cincinnati or Lexington — just shipped from the local UPS.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "morehead-1",
      name: "Morehead Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "120 E First St",
      city: "Morehead",
      state: "KY",
      zip: "40351",
      phone: "(606) 783-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Morehead",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Rowan County. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "morehead-2",
      name: "Mount Sterling Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "110 E Main St",
      city: "Mount Sterling",
      state: "KY",
      zip: "40353",
      phone: "(859) 498-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "25 miles from Morehead",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Montgomery County and western Rowan County. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "morehead-3",
      name: "Rowan County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "600 E Main St",
      city: "Morehead",
      state: "KY",
      zip: "40351",
      phone: "(606) 784-7513",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.1 miles from Rowan County Courthouse",
      website: "https://www.morehead-ky.gov/",
      websiteLabel: "Rowan County Clerk",
      notes: "Major county facility for Rowan County. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
    {
      id: "morehead-4",
      name: "Lexington Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1055 New Circle Rd",
      city: "Lexington",
      state: "KY",
      zip: "40511",
      phone: "(859) 252-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "65 miles from Morehead",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for the Lexington area. High volume — book appointments 2+ weeks in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "morehead-5",
      name: "Ashland Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1500 Greenup Ave",
      city: "Ashland",
      state: "KY",
      zip: "41101",
      phone: "(606) 324-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "55 miles from Morehead",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Boyd County and northeastern Rowan County. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "morehead-6",
      name: "Winchester Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "120 N Main St",
      city: "Winchester",
      state: "KY",
      zip: "40391",
      phone: "(859) 744-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "50 miles from Morehead",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Clark County and southern Rowan County. Limited weekend hours.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 6 hour drive to Atlanta", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Rowan County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Rowan County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Morehead applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Atlanta regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Rowan County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Rowan County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Morehead." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Morehead address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Morehead before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Morehead for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 6 hour drive to Atlanta." },
    { q: "How fast can I renew my passport in Morehead?", a: "Fast Passport Center offers fast passport renewal in Morehead in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my Appalachian trail international trip?", a: "If your passport expires before an international trip, contact Fast Passport Center immediately. Many Morehead travelers visit Canada or take international trips. We can expedite your passport renewal in Morehead in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Morehead?", a: "Same-day passport appointments are only available at the Atlanta Regional Passport Agency — a 6 hour drive from Morehead — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Rowan County residents." },
    { q: "Can I expedite a child passport in Morehead?", a: "Yes. Fast Passport Center expedites child passports in Morehead for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Rowan County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Morehead with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Morehead?", a: "With Fast Passport Center's emergency courier service, Morehead travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to drive to the Atlanta Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Morehead?", a: "Popular options include the Morehead Post Office (40351), Rowan County Clerk (40351), Mount Sterling Post Office (40353), Lexington Main Post Office (40511), Ashland Post Office (41101), and Winchester Post Office (40391). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Morehead?", a: "Same-day passports are only available at the Atlanta Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option — without the 6 hour drive to Atlanta." },
    { q: "What if I live in a smaller Rowan County town?", a: "We serve the entire Rowan County and surrounding area including Mount Sterling, West Liberty, Salyersville, Paintsville, Prestonsburg, Lexington, Ashland, Huntington (WV), Maysville, Winchester, and Paris. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Morehead?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
    { q: "Do you help with passport photos in Morehead?", a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and Walmart locations in Morehead and Lexington offer passport photo services. We review your photos before submission to ensure they meet State Department standards." },
    { q: "What documents do I need for a first-time passport in Morehead?", a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk." },
    { q: "Can you help with a child's passport in Morehead?", a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Rowan County families through the entire process and help schedule acceptance clerk appointments." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Morehead", address: "601 Flemingsburg Rd, Morehead, KY 40351", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Morehead", address: "200 Walmart Plaza Dr, Morehead, KY 40351", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Morehead", address: "100 Stone St, Morehead, KY 40351", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Rite Aid — Morehead", address: "515 E Main St, Morehead, KY 40351", hours: "Mon–Sun: 8AM–10PM" },
    { name: "CVS Pharmacy — Lexington", address: "3151 Richmond Rd, Lexington, KY 40509", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Lexington", address: "500 W New Circle Rd, Lexington, KY 40511", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Ashland", address: "1620 13th St, Ashland, KY 41101", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Lexington", address: "1650 Bryan Station Rd, Lexington, KY 40505", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "Circuit Court Clerk — Rowan County",
      address: "600 E Main St, Morehead, KY 40351",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Driver's license and REAL ID. Appointment recommended.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Montgomery County",
      address: "1 Courthouse Sq, Mount Sterling, KY 40353",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Alternative for Montgomery County and western Rowan County residents.",
      featured: false,
    },
    {
      name: "Circuit Court Clerk — Clark County",
      address: "34 S Main St, Winchester, KY 40391",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center for Clark County and southern Rowan County residents.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Boyd County",
      address: "2800 Louisa St, Catlettsburg, KY 41129",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Alternative for Boyd County and northeastern Rowan County residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "LEX",
      name: "Blue Grass Airport",
      distance: "65 miles from Morehead",
      address: "4000 Terminal Dr, Lexington, KY 40510",
      note: "The primary airport for Rowan County. Regional and limited international flights.",
      flightUrl: "https://www.bluegrassairport.com/",
      flightLabel: "Lexington Airport",
      featured: true,
    },
    {
      code: "HTS",
      name: "Tri-State Airport",
      distance: "60 miles from Morehead",
      address: "1449 Airport Rd, Huntington, WV 25704",
      note: "Alternative regional airport for international flights. Accessible via I-64.",
      flightUrl: "https://www.tristateairport.com/",
      flightLabel: "Tri-State Airport",
      featured: false,
    },
    {
      code: "CVG",
      name: "Cincinnati/Northern Kentucky International",
      distance: "110 miles from Morehead",
      address: "3087 Terminal Dr, Hebron, KY 41048",
      note: "Major international hub for long-haul flights to Europe, Asia, and the Caribbean. Accessible via I-64.",
      flightUrl: "https://www.cvgairport.com/",
      flightLabel: "CVG Airport",
      featured: false,
    },
  ],
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Birth & Death Certificates", desc: "Kentucky Office of Vital Statistics in Frankfort — order certified copies online, by mail, or in person for passport applications and name changes.", url: "https://www.chfs.ky.gov/agencies/dph/dehp/vsb/Pages/default.aspx" },
    { icon: "ri-heart-line", label: "Marriage Records", desc: "Rowan County Clerk — certified marriage licenses for name change passport applications and legal documentation.", url: "https://www.morehead-ky.gov/" },
    { icon: "ri-scales-line", label: "Divorce Decrees", desc: "Rowan Circuit Court Clerk — certified divorce decrees supporting passport name change or correction.", url: "https://kycourts.gov/Courts/Circuit-Court/Pages/Rowan.aspx" },
    { icon: "ri-global-line", label: "VitalChek — Nationwide Records", desc: "If your record was issued outside Kentucky, order certified copies online through VitalChek for delivery in 5–10 business days.", url: "https://www.vitalchek.com/" },
  ],
};